/**
 * API Service for Curiosity Centre - Student Portal
 * Handles all API calls related to Curiosity Centre for students
 */
import { apiUrl } from '../../api/config';
import axios from 'axios';
import { getAuthToken } from '../../utils/auth';

const BASE_URL = '/v1/student/curiosity';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = getAuthToken('student');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Helper to handle API errors
const handleError = (error) => {
  if (error.response) {
    // Server responded with error status
    throw new Error(error.response.data?.detail || error.response.data?.message || 'An error occurred');
  } else if (error.request) {
    // Request made but no response
    throw new Error('Network error. Please check your connection.');
  } else {
    // Something else happened
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

export const curiosityAPI = {
  /**
   * Get all content for a chapter (organized by type)
   * @param {number|string} chapterId - Chapter ID
   * @returns {Promise<Object>} Organized chapter content
   */
  getChapterContent: async (chapterId, subjectId = null) => {
    try {
      // Use 0 as chapter_id if not provided to allow optional chapter_id in URL
      const effectiveChapterId = chapterId || 0;
      const params = {};
      if (!chapterId && subjectId) {
        params.subject_id = subjectId;
      }
      const response = await axios.get(
        apiUrl(`${BASE_URL}/chapter/${effectiveChapterId}/`),
        { 
          params,
          headers: getAuthHeaders() 
        }
      );
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  /**
   * Get single content item by ID
   * Automatically records a "viewed" interaction
   * @param {number|string} contentId - Content ID
   * @returns {Promise<Object>} Content details
   */
  getContent: async (contentId) => {
    try {
      const response = await axios.get(
        apiUrl(`${BASE_URL}/content/${contentId}/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  /**
   * Get video transcript for a content item
   * @param {number|string} contentId - Content ID (must be video type)
   * @returns {Promise<Object>} Transcript data with segments
   */
  getTranscript: async (contentId) => {
    try {
      const response = await axios.get(
        apiUrl(`${BASE_URL}/content/${contentId}/transcript/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  /**
   * Record student interaction with content
   * @param {Object} data - Interaction data
   * @param {number} data.content_id - Content ID (optional)
   * @param {number} data.topic_id - Topic ID (optional)
   * @param {number} data.news_id - News ID (optional)
   * @param {string} data.interaction_type - Type: 'viewed', 'clicked', 'completed', 'time_spent'
   * @param {number} data.time_spent_seconds - Time spent in seconds (optional)
   * @param {number} data.completion_percentage - Completion percentage 0-100 (optional)
   * @returns {Promise<Object>} Created interaction record
   */
  recordInteraction: async (data) => {
    try {
      const response = await axios.post(
        apiUrl(`${BASE_URL}/interact/`),
        data,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      // Don't throw error for interaction tracking failures (non-critical)
      console.warn('Failed to record interaction:', error);
      return null;
    }
  },

  /**
   * Get topic with all resources
   * @param {number|string} topicId - Topic ID
   * @returns {Promise<Object>} Topic details with nested resources
   */
  getTopic: async (topicId) => {
    try {
      const response = await axios.get(
        apiUrl(`${BASE_URL}/topics/${topicId}/`),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      handleError(error);
    }
  },

  /**
   * Get latest news for a subject
   * @param {number|string} subjectId - Subject ID
   * @param {Object} params - Optional query parameters
   * @param {number} params.limit - Number of news items to return (default: 10)
   * @returns {Promise<Array|Object>} News items (array or paginated response)
   */
  getNewsForSubject: async (subjectId, params = {}) => {
    try {
      const response = await axios.get(
        apiUrl(`${BASE_URL}/news/subject/${subjectId}/`),
        {
          headers: getAuthHeaders(),
          params: {
            limit: params.limit || 10,
            ...params,
          },
        }
      );
      // Handle both array and paginated responses
      return response.data.results || response.data || [];
    } catch (error) {
      handleError(error);
    }
  },

  /**
   * Get user's interactions with content
   * @param {Object} params - Query parameters (content, topic, news_article, interaction_type)
   * @returns {Promise<Array|Object>} Interactions (array or paginated response)
   */
  getInteractions: async (params = {}) => {
    try {
      const response = await axios.get(
        apiUrl(`${BASE_URL}/interact/`),
        {
          headers: getAuthHeaders(),
          params,
        }
      );
      // Handle both array and paginated responses
      return response.data.results || response.data || [];
    } catch (error) {
      handleError(error);
    }
  },
};

